import { Config, CompanyInfo, CompanyStats, Template } from "../../types";
import { mockConfig } from "../components/constants/mockConfig";
import { redirect } from "next/navigation";
import { cache } from "react";

function extractValidPhones(input: string | string[]): string[] | null {
  try {
    let phoneArray: string[];
    // Handle stringified array
    if (
      typeof input === "string" &&
      input.trim().startsWith("[") &&
      input.trim().endsWith("]")
    ) {
      phoneArray = JSON.parse(input);
    }
    // Handle already-parsed array
    else if (Array.isArray(input)) {
      phoneArray = input;
    } else {
      return null;
    }
    // Clean & filter (safe for only strings)
    const validPhones = phoneArray
      .filter((item) => typeof item === "string") // skip non-strings
      .map((item) => item.trim())
      .filter((item) => item !== "+234");

    return validPhones.length === 0 ? null : validPhones;
  } catch (err) {
    if (err instanceof Error) {
      throw new Error(err.message);
    }
    return null;
  }
}
function cleanCompanySocials(company: CompanyInfo): CompanyInfo {
  const defaultPatterns: Record<string, string> = {
    facebook: "https://facebook.com/",
    x: "https://x.com/",
    instagram: "https://instagram.com/",
    linkedin: "https://www.linkedin.com/company/",
    youtube: "https://www.youtube.com/@",
    tiktok: "https://tiktok.com/",
  };

  // Iterate over social media keys to check and clean defaults
  for (const key in defaultPatterns) {
    if (company[key as keyof CompanyInfo] === defaultPatterns[key]) {
      // Use an explicit cast to handle the nullable type
      (company[key as keyof CompanyInfo] as string | null) = null;
    }
  }

  return company;
}

type TemplateType = "template1" | "template2" | "template3";
function getDesignFromTemplate(template: TemplateType): Template {
  const designMap: Record<TemplateType, Template> = {
    template1: "design1",
    template2: "design2",
    template3: "design3",
  };
  // return "design1";
  return designMap[template];
}

// keep your existing imports...

export const fetchConfig = cache(
  async (companyIdentifier?: string): Promise<Config> => {
    console.log({ companyIdentifier });

    const isDemo = process.env.NEXT_PUBLIC_DEMO_MODE === "true";
    const BASE_URL = process.env.NEXT_PUBLIC_BASE_URL;

    if (isDemo) return mockConfig;
    console.log(`${BASE_URL}api/v1/custom/company-setting/${companyIdentifier}`);

    try {
      const res = await fetch(
        `${BASE_URL}api/v1/custom/company-setting/${companyIdentifier}`,
        {
          next: {
            revalidate: 10, // ISR cache at fetch level
          },
        }
      );

      if (!res.ok) {
        const errorData = await res.json();

        if (
          errorData.message ===
          "Company settings not found for the given domain."
        ) {
          console.log("expected");
          return {
            res: { status: 404 },
            ...mockConfig,
          } as Config;
        }
      }

      const data = await res.json();
      const settings = data?.setting || {};

      const companyInfo: CompanyInfo =
        cleanCompanySocials(settings.company_info) || {};

      const fullAddress = `${companyInfo.head_office_address} ${companyInfo.city}, ${companyInfo.local_government}, ${companyInfo.state}`;
      companyInfo.full_address = fullAddress;
      companyInfo.head_office_address = fullAddress;
      companyInfo.phone_number = extractValidPhones(
        companyInfo.phone_number
      ) as CompanyInfo["phone_number"];

      const companyServices = Object.values(settings.company_services)
        .flat()
        .filter((item) => item !== "N/A") as string[];

      const companyStat: CompanyStats = {
        total_properties: data.setting.total_properties,
        years_in_industry: `${Math.round(
          data.setting.company_info.year_of_experience
        )} years +`,
        total_branch: data.setting.total_branch,
        total_staff: data.setting.total_staff,
        properties_for_rent: data.setting.total_properties,
        properties_for_sale: 0,
        properties_for_short_let: 0,
        total_units_managing: data.setting.unit_count,
        total_reviews: data.setting.total_review,
        completed_transaction: data.setting.transaction_count,
      };

      const transformedConfig: Config = {
        agentId: "ademolatech_binary001",
        template: getDesignFromTemplate(
          settings.website_settings?.web_template as TemplateType
        ) as Template,
        styling: {
          primaryColor:
            settings.website_settings?.color_scheme === "default"
              ? "#2563EB"
              : settings.website_settings?.color_scheme,
          secondaryColor: "#2563EB",
          fontFamily: settings.website_settings?.website_font || "LOOS",
          fontSizeBase: "16px",
          h1Size: "44px",
          h2Size: "32px",
          h3Size: "28px",
          h4Size: "22px",
          h5Size: "18px",
          h6Size: "14px",
          backgroundImage: undefined,
        },
        metadata: {
          siteName: companyInfo.name || "Ademolatech Real Estate",
          description: `Explore properties with ${companyInfo.name || "Ademolatech"
            }`,
          keywords: "real estate",
        },
        websiteSettings: {
          sponsored_logo: settings.website_settings?.sponsored_logo,
          modules_listing: settings.website_settings?.modules_listing,
          about_us_display: settings.website_settings?.about_us_display,
          services_contact_page:
            settings.website_settings?.services_contact_page,
          staffs_branch_options:
            settings.website_settings?.staffs_branch_options,
          social_link_visibility:
            settings.website_settings?.social_link_visibility,
          rent_properties: settings.website_settings?.rent_properties,
          sale_properties: settings.website_settings?.sale_properties,
          shortlet_properties: settings.website_settings?.shortlet_properties,
          typography: settings.website_settings?.typography,
        },
        companyInfo,
        companyServices,
        companyStat,
      };

      return transformedConfig;
    } catch (error: unknown) {
      if (
        error instanceof Error &&
        error.message === "Company settings not found for the given domain."
      ) {
        redirect("https://ourlisting.ng");
      }

      console.log("error", { error });

      throw new Error("Something went wrong fetching config");
    }
  }
);
